/**
 * EXECUTION RUNTIME — presentation primitives.
 *
 * These render backend-owned values. Not one of them computes a state, counts
 * anything, aggregates anything, or assumes a payload shape: each takes the
 * value it displays as a prop, so the same component serves the Execution
 * Barrier Map, the Decision Twin, the Decision Episode and the field surfaces
 * as those screens are built, without a rewrite between them.
 *
 * What they are FOR, beyond consistency: the rules in `runtimeVocabulary` are
 * enforced in one place instead of being re-remembered by every panel.
 * UNKNOWN never reads as safe. CONFLICT never reads as failure. Nothing
 * carries a score. Running work never appears startable. A scenario never
 * looks like live state.
 */
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  CheckCircle2,
  OctagonAlert,
  AlertTriangle,
  HelpCircle,
  GitCompareArrows,
  Info,
  ShieldCheck,
  FileText,
  Hammer,
  CircleCheckBig,
  FlaskConical,
  Archive,
  Radio,
  Unplug,
} from "lucide-react";
import {
  runtimeStateDisplay,
  truthStateDisplay,
  temporalityDisplay,
  presentSourceReference,
  presentContractVersion,
} from "./runtimeVocabulary";

const TONE_CLASS = {
  good: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  attention: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  bad: "bg-red-500/20 text-red-400 border-red-500/30",
  unknown: "bg-muted text-muted-foreground border-border",
  /** Running work — deliberately not the green of "can start now". */
  active: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  /** Finished work — settled, not celebratory. */
  done: "bg-slate-500/20 text-slate-300 border-slate-500/30",
};

const RUNTIME_STATE_ICON = {
  CAN_START_NOW: CheckCircle2,
  CAN_START_CONDITIONALLY: AlertTriangle,
  BLOCKED: OctagonAlert,
  UNKNOWN: HelpCircle,
  IN_PROGRESS: Hammer,
  COMPLETE: CircleCheckBig,
};

const TRUTH_ICON = {
  PASS: CheckCircle2,
  FAIL: OctagonAlert,
  UNKNOWN: HelpCircle,
  CONFLICT: GitCompareArrows,
};

const TEMPORALITY_ICON = {
  CURRENT: Radio,
  HYPOTHETICAL: FlaskConical,
  AS_RECORDED: Archive,
};

const TEMPORALITY_CLASS = {
  current: "text-muted-foreground",
  hypothetical: "text-violet-400 border-violet-500/40 bg-violet-500/10",
  historical: "text-amber-400 border-amber-500/40 bg-amber-500/10",
};

/**
 * Which of the six runtime states this work is in, as the backend decided it.
 *
 * The meaning travels with the badge for assistive technology, because the
 * distinctions that matter most — UNKNOWN is an open question, IN_PROGRESS is
 * not an invitation to mobilize — are exactly the ones a colour cannot carry.
 */
export const RuntimeStateBadge = ({ state, className = "" }) => {
  const display = runtimeStateDisplay(state);
  const Icon = RUNTIME_STATE_ICON[state] || HelpCircle;
  return (
    <Badge
      variant="outline"
      className={`${TONE_CLASS[display.tone]} font-medium ${className}`}
      title={display.meaning}
      data-testid="runtime-state"
    >
      <Icon className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
      {display.label}
      <span className="sr-only"> — {display.meaning}</span>
    </Badge>
  );
};

/**
 * The truth state of one evaluated fact.
 *
 * CONFLICT gets its own icon and its own amber, not the red of FAIL: the two
 * lead to different work. A failed condition needs the condition met; a
 * conflicted one needs a human to read two sources and decide which is right.
 */
export const TruthStateBadge = ({ state, className = "" }) => {
  const display = truthStateDisplay(state);
  const Icon = TRUTH_ICON[state] || HelpCircle;
  return (
    <Badge
      variant="outline"
      className={`${TONE_CLASS[display.tone]} font-normal text-[11px] ${className}`}
      title={display.meaning}
      data-testid="truth-state"
    >
      <Icon className="w-3 h-3 mr-1" aria-hidden="true" />
      {display.label}
      <span className="sr-only"> — {display.meaning}</span>
    </Badge>
  );
};

/**
 * Where a fact came from.
 *
 * The runtime identifies a constraint by the row that determines it, and
 * those row ids are database identities — useful for diagnostics, meaningless
 * to a superintendent standing at a gang box. So the human label leads and
 * the identity sits underneath, shown only when asked for.
 */
export const EvidenceReference = ({ sourceTable, sourceIds, label, className = "" }) => {
  const source = presentSourceReference({ sourceTable, sourceIds, label });
  if (!source.named) {
    return (
      <span className={`text-[11px] text-muted-foreground ${className}`}>
        No source is named for this.
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${className}`}>
      <FileText className="w-3 h-3 text-muted-foreground shrink-0" aria-hidden="true" />
      <span className="text-muted-foreground">
        {source.label || source.table}
        {source.count > 1 ? ` · ${source.count} records` : ""}
      </span>
      {/* The raw identity is diagnostics. Available, never the headline. */}
      {source.detail && (
        <span className="sr-only" data-testid="evidence-detail">
          {source.detail}
        </span>
      )}
    </span>
  );
};

/**
 * One stated fact with its truth state and its source.
 *
 * The three travel together on purpose: a state with no source is an
 * assertion, and a source with no state is trivia.
 */
export const SourceFact = ({ statement, state, sourceTable, sourceIds, sourceLabel, className = "" }) => (
  <div className={`flex flex-wrap items-start gap-x-2 gap-y-1 min-w-0 ${className}`}>
    <TruthStateBadge state={state} className="shrink-0" />
    <span className="text-[12px] break-words min-w-0">{statement}</span>
    <EvidenceReference sourceTable={sourceTable} sourceIds={sourceIds} label={sourceLabel} />
  </div>
);

/**
 * What OMNΛS could not establish, named rather than omitted.
 *
 * An empty list renders nothing — this must never announce "no missing
 * information", which is itself a claim the caller may not be able to make.
 */
export const MissingInformationCallout = ({ items = [], title = "What is not established" }) => {
  const notes = (items || []).filter(Boolean);
  if (notes.length === 0) return null;
  return (
    <Alert className="border-border bg-muted/20" data-testid="missing-information">
      <Info className="h-4 w-4" />
      <AlertDescription className="space-y-1">
        <p className="text-[11px] font-medium">{title}</p>
        {notes.map((note, index) => (
          <p key={index} className="text-[10px] text-muted-foreground">
            {typeof note === "string" ? note : JSON.stringify(note)}
          </p>
        ))}
      </AlertDescription>
    </Alert>
  );
};

/**
 * Who has to act, when the backend names an authority.
 *
 * Rendered only when there is one. Inventing an authority would tell someone
 * to go and find a person the system never named.
 */
export const AuthorityRequired = ({ authority, detail, className = "" }) => {
  if (!authority) return null;
  return (
    <p
      className={`text-[11px] text-muted-foreground flex items-start gap-1 ${className}`}
      data-testid="authority-required"
    >
      <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
      <span>
        Requires <span className="text-foreground">{authority}</span>
        {detail ? ` — ${detail}` : ""}
      </span>
    </p>
  );
};

/**
 * One constraint, as a card.
 *
 * Everything it shows is a prop. It renders no count it was not given, sorts
 * nothing, and carries no notion of which constraint matters more than
 * another — ordering is the caller's, and a merged contract's, to decide.
 *
 * `consequence` is the one field with real teeth: "if resolved alone, one
 * activity becomes executable" is a backend conclusion about leverage. It is
 * displayed verbatim when given and never computed here. `exposure` is the
 * counterfactual upper bound and is labelled as one — never as priority.
 */
export const ConstraintCard = ({
  title,
  state,
  statement,
  sourceTable,
  sourceIds,
  sourceLabel,
  affects,
  consequence,
  exposure,
  authority,
  authorityDetail,
  children,
  className = "",
}) => (
  <div
    className={`rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2 min-w-0 ${className}`}
    data-testid="constraint-card"
  >
    <div className="flex flex-wrap items-start justify-between gap-2">
      <p className="text-sm font-medium break-words min-w-0">{title}</p>
      <TruthStateBadge state={state} className="shrink-0" />
    </div>

    {statement && <p className="text-[12px] text-muted-foreground break-words">{statement}</p>}

    {/* What this constraint touches — the backend's own count, never a tally
        this component made. */}
    {affects && <p className="text-[11px] text-muted-foreground">{affects}</p>}

    {/* The leverage statement, verbatim. No score, no ordering, no
        "resolve this first". */}
    {consequence && (
      <p className="text-[11px] text-foreground border-l-2 border-border pl-2">{consequence}</p>
    )}

    {/* Reachability over declared edges. Said as an upper bound, in words,
        every time — a bare number here would read as importance. */}
    {exposure && (
      <p className="text-[11px] text-muted-foreground border-l-2 border-border/50 pl-2">
        {exposure}
        <span className="block text-[10px] italic">
          Upper bound on work sitting behind this. It is not a statement about what is causing
          what, and not an instruction about what to do first.
        </span>
      </p>
    )}

    <AuthorityRequired authority={authority} detail={authorityDetail} />
    <EvidenceReference sourceTable={sourceTable} sourceIds={sourceIds} label={sourceLabel} />

    {children}
  </div>
);

/**
 * Whether a panel is showing live state, a scenario, or a frozen record.
 *
 * The word is always rendered — never colour alone — because the three are
 * not decoration. A hypothetical that reads as current invites someone to act
 * on a future that was never applied; a record that reads as current invites
 * someone to act on a fact that has since moved.
 */
export const TemporalityMarker = ({ temporality, at, className = "" }) => {
  const display = temporalityDisplay(temporality);
  const Icon = TEMPORALITY_ICON[temporality] || TEMPORALITY_ICON.CURRENT;
  const emphatic = display.tone !== "current";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium ${
        emphatic ? `border rounded px-1.5 py-0.5 ${TEMPORALITY_CLASS[display.tone]}` : TEMPORALITY_CLASS.current
      } ${className}`}
      title={display.meaning}
      data-testid="temporality-marker"
    >
      <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
      {display.mark}
      {at ? ` · ${at}` : ""}
      <span className="sr-only"> — {display.meaning}</span>
    </span>
  );
};

/**
 * A scenario, marked as one.
 *
 * `counterfactual-execution-1` writes nothing and labels every response
 * HYPOTHETICAL. This says so in words at the top of the thing itself, rather
 * than trusting a tint at the edge of a panel: the whole point of the
 * simulator is that a reader can tell it apart from the project.
 */
export const HypotheticalMarker = ({ appliedToCanonicalState = false, className = "" }) => (
  <span
    className={`inline-flex flex-wrap items-center gap-1.5 text-[10px] ${className}`}
    data-testid="hypothetical-marker"
  >
    <TemporalityMarker temporality="HYPOTHETICAL" />
    <span className="text-muted-foreground">
      {appliedToCanonicalState
        ? "This scenario has been applied."
        : "Nothing here has been applied to the project."}
    </span>
  </span>
);

/**
 * The server answered with a contract this screen was not written against.
 *
 * Rendered instead of guessing. An additive version would probably render
 * correctly by luck and a breaking one would render confidently wrong, so the
 * screen says which version it knows and which one arrived, and leaves the
 * judgement to a human.
 */
export const ContractMismatchCallout = ({ received, supported, className = "" }) => {
  const version = presentContractVersion(received, supported);
  if (version.matches) return null;
  return (
    <Alert
      className={`border-amber-500/40 bg-amber-500/10 ${className}`}
      data-testid="contract-mismatch"
    >
      <Unplug className="h-4 w-4" />
      <AlertDescription className="space-y-1">
        <p className="text-[11px] font-medium">{version.message}</p>
        <p className="text-[10px] text-muted-foreground">
          What is shown below may be incomplete. Nothing has been guessed to fill the gap.
        </p>
      </AlertDescription>
    </Alert>
  );
};
